import { Button, HStack } from '@chakra-ui/react'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'
import ProviderDetails from 'components/Scheduling/ProviderDetails'
import TimingDetails from 'components/Scheduling/TimingDetails'
import LoadingState from 'components/Spinner'
import { RoutePaths } from 'containers/Core/Routes'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { addMinutes } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import withAccount, { WithAccountProps } from 'hoc/withAccount'
import useSearchParams from 'hooks/useSearchParams'
import { useFlags } from 'launchdarkly-react-client-sdk'
import momentTZ from 'moment-timezone'
import { memo, useCallback } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { AppointmentModifyEventProps } from 'utils/amplitude.types'
import {
  isInitialMedical as isInitialMedicalUtils,
  isInitialNutrition as isInitialNutritionUtils,
  useIsInsurancePatient,
} from 'utils/program.utils'
import { toGCal, toICS } from 'utils/time.utils'
import LegalText from './LegalText'
import { getPatientFacingAppointmentName } from './utils'

export type CalendarDetails = {
  dateTime: Date
  endDateTime: Date
  title: string
  description: string
  location: string
}

const SuccessScreen: React.FC<WithAccountProps> = ({ user }) => {
  const {
    provider,
    dateTime,
    timeZone,
    appointment,
    isReschedule,
    appointmentType,
    participantsChanged,
    rebookingFlowData,
  } = useSchedulingContext()
  const history = useHistory()
  const { noFeeInsuranceMembership } = useFlags()
  const isInsurancePatient = useIsInsurancePatient(user)
  const searchParams = useSearchParams()
  const source = searchParams.get('source')
  const isInitialMedical = isInitialMedicalUtils(appointmentType ?? null)
  const isInitialNutrition = isInitialNutritionUtils(appointmentType ?? null)

  const getAppointmentName = useCallback(
    () => getPatientFacingAppointmentName(appointmentType?.name),
    [appointmentType?.name, getPatientFacingAppointmentName]
  )

  const getCalendarDetails = useCallback(
    (seperator: string): CalendarDetails | undefined => {
      if (
        !provider ||
        !dateTime ||
        !timeZone ||
        !appointment ||
        !appointmentType
      ) {
        return undefined
      }

      const endDateTime = addMinutes(dateTime, appointmentType.duration)
      const timeZoneShorthand = momentTZ.tz(timeZone).format('z')
      const timeZoneOffset = momentTZ.tz(timeZone).format('Z')
      const zonedDate = formatInTimeZone(
        dateTime,
        timeZone,
        'EEEE, MMMM d, yyyy'
      )
      const zonedStartDate = formatInTimeZone(dateTime, timeZone, 'h:mm	aaa')
      const zonedEndDate = formatInTimeZone(endDateTime, timeZone, 'h:mm	aaa')

      const providerName = `${provider.first_name} ${provider.last_name}`
      const videoCallUrl = appointment.video_call?.join_url ?? ''
      const title = `Allara ${getAppointmentName()} with ${providerName}`
      const description = [
        `Allara ${getAppointmentName()} with ${providerName}`,
        `Click to join: ${videoCallUrl}`,
        `${timeZoneShorthand} (GMT ${timeZoneOffset})`,
        `${zonedDate}`,
        `${zonedStartDate} - ${zonedEndDate}`,
      ].join(seperator)

      return {
        dateTime,
        endDateTime,
        title,
        description,
        location: videoCallUrl,
      }
    },
    [provider, dateTime, timeZone, appointment, appointmentType]
  )

  const handleDownloadICS = useCallback(() => {
    // Seperator used to join the description
    const ESCAPED_SEPARATOR =
      navigator.appVersion.indexOf('Win') !== -1 ? `\\r\\n` : `\\n`

    const calendarDetails = getCalendarDetails(ESCAPED_SEPARATOR)

    if (!calendarDetails || !appointment || !appointmentType) {
      return alert('Could not save ICS file')
    }

    // Download the ICS file
    const rawICS = toICS(`${appointment.id}@source`, calendarDetails)
    const blob = new Blob([rawICS])
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    a.download = `allara_${getAppointmentName().replace(
      / /g,
      '_'
    )}_appointment.ics`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }, [getCalendarDetails])

  const handleAddToGCal = useCallback(() => {
    // Seperator used to join the description
    const SEPARATOR = navigator.appVersion.indexOf('Win') !== -1 ? `\r\n` : `\n`

    const calendarDetails = getCalendarDetails(SEPARATOR)

    if (!calendarDetails) {
      return alert('Could not create event')
    }

    window.open(toGCal(calendarDetails), '_blank')
  }, [getCalendarDetails])

  const shouldGoToDashboard =
    !noFeeInsuranceMembership ||
    !(
      isInsurancePatient &&
      ((isInitialMedical && source === 'onboarding') ||
        (isInitialNutrition && source === 'initial_book_md_done'))
    )

  const returnToDashboard = useCallback(
    () => history.push(RoutePaths.DASHBOARD + `?from=scheduling`),
    []
  )

  const goToHowDidYouHear = useCallback(() => {
    history.push(RoutePaths.HOW_DID_YOU_HEAR + `?from=scheduling`)
  }, [])

  if (!appointmentType) {
    return <LoadingState />
  } else if (!provider || !dateTime || !timeZone || !appointment) {
    return <Redirect to={RoutePaths.SCHEDULING} />
  }

  return (
    <div className="success screen-container">
      <CloseIcon
        className="cursor-pointer d-none d-lg-block position-absolute top-3 right-6 m-3 fs-1"
        onClick={shouldGoToDashboard ? returnToDashboard : goToHowDidYouHear}
        width="18px"
        height="18px"
      />
      <h1
        style={{
          fontFamily: 'Instrument Serif',
          fontWeight: 400,
          fontSize: '32px',
        }}
      >
        {rebookingFlowData
          ? 'Your appointment is rebooked!'
          : isReschedule
          ? 'Your appointment is rescheduled!'
          : 'Your appointment is scheduled!'}
      </h1>
      <h1 className="ff-inter-medium fs-7 mb-4">
        {getAppointmentName()}, {appointmentType.duration} min
      </h1>
      {appointment.status === 'canceled' && (
        <div className="alert ff-inter fs-2">
          This appointment has been canceled
        </div>
      )}
      <div className="d-flex flex-column mb-3">
        <div className="d-flex flex-wrap align-items-center">
          <ProviderDetails
            provider={provider}
            className="align-items-center mr-8 mb-1"
          />
          <TimingDetails
            date={dateTime}
            timeZone={timeZone}
            appointmentType={appointmentType}
          />
        </div>
        {appointment.status !== 'canceled' &&
          participantsChanged === false &&
          appointment.video_call?.join_url !== undefined && (
            <HStack wrap="wrap" gap="6" paddingTop="3" paddingBottom="3">
              <Button
                variant="outline"
                colorScheme={'var(--blue-dark)'}
                _hover={{
                  backgroundColor: 'var(--blue-dark)',
                  cursor: 'pointer',
                  color: 'white',
                }}
                height="35px"
                onClick={handleDownloadICS}
              >
                Add to iCal/Outlook
              </Button>
              <Button
                variant="outline"
                colorScheme={'var(--blue-dark)'}
                _hover={{
                  backgroundColor: 'var(--blue-dark)',
                  cursor: 'pointer',
                  color: 'white',
                }}
                height="35px"
                onClick={handleAddToGCal}
              >
                Add to Google
              </Button>
            </HStack>
          )}
        <LegalText />
      </div>
    </div>
  )
}

export default memo(withAccount(SuccessScreen))
